import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Form, InputNumber, Modal } from 'antd';

import DatePicker from 'components/DatePicker/DatePicker';
import { formatDateToSubmit } from 'utilities/datesAndTimes';

import { isAfterTodayPaymentCutoff } from './offerHelpers';

const { Item } = Form;

const EditOfferPaymentModal = ({
  transactions,
  initialValues,
  setInitialValues,
  type,
  hide,
  expirationDate,
  onEditPayment,
  onAddPayment,
  onDeletePayment,
  formId = 'editPaymentForm',
  ...modalProps
}) => {
  const [form] = Form.useForm();
  const [updatedPaymentDateExists, setUpdatedPaymentDateExists] =
    useState(false);

  const onDateChange = (date) => {
    const paymentDateExists = transactions.find((transaction) =>
      dayjs(transaction.date).isSame(date, 'day'),
    );

    if (paymentDateExists) {
      return setUpdatedPaymentDateExists(true);
    }

    setUpdatedPaymentDateExists(false);
  };

  const onClose = () => {
    form.resetFields();
    setInitialValues({});
    hide();
  };

  const onDelete = () => {
    onDeletePayment(initialValues.date);
    onClose();
  };

  const onFinish = async ({ amount, date }) => {
    await form.validateFields();

    const payment = {
      amount: Math.round(amount * 100),
      date: formatDateToSubmit(date),
    };

    if (type === 'edit') {
      onEditPayment(initialValues.date, payment);
    }

    if (type === 'add') {
      onAddPayment(payment);
    }

    onClose();
  };

  const buttons = [
    <Button form={formId} type="primary" htmlType="submit" key="confirm">
      Confirm Payment Details
    </Button>,
  ];

  if (type === 'edit' && onDeletePayment) {
    buttons.unshift(
      <Button key="delete" danger onClick={onDelete}>
        Delete This Payment
      </Button>,
    );
  }

  useEffect(() => {
    form.setFieldsValue({
      amount: initialValues.amount,
      date: initialValues.date,
    });
  }, [initialValues]);

  return (
    <Modal
      {...modalProps}
      title="Edit Payment"
      onCancel={onClose}
      footer={buttons}
    >
      <Form id={formId} name={formId} form={form} onFinish={onFinish}>
        <Item
          label="Amount"
          name="amount"
          rules={[
            {
              required: true,
              message: 'Please enter a payment amount.',
            },
          ]}
        >
          <InputNumber min="0.00" step="0.01" />
        </Item>
        <Item
          label="Date"
          name="date"
          rules={[
            {
              required: true,
              message: 'Please enter a payment date.',
            },
            {
              validator: () => {
                return new Promise((resolve, reject) => {
                  if (updatedPaymentDateExists) {
                    reject();
                  }
                  resolve();
                });
              },
              message:
                'The customer already has a payment scheduled for this date. Please select a different date.',
            },
          ]}
        >
          <DatePicker
            onChange={onDateChange}
            disabledDate={(d) =>
              !d ||
              d.isSameOrBefore(dayjs()) ||
              d.isSameOrAfter(expirationDate) ||
              isAfterTodayPaymentCutoff(d)
            }
            style={{ marginBottom: 'var(--spacing-sm)' }}
          />
        </Item>
      </Form>
    </Modal>
  );
};

export default EditOfferPaymentModal;
